import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle
} from '@/components/ui/sheet';
import { useCartStore } from '@/store/cartStore';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CartItemRow } from './CartItemRow';
import { EmptyCartState } from './EmptyCartState';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onRemoveItem: (itemId: string) => void;
  isUpdating?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

export function CartDrawer({ 
  isOpen, 
  onClose,
  onUpdateQuantity,
  onRemoveItem,
  isUpdating = false,
  error = null,
  onRetry
}: CartDrawerProps) {
  const { t, i18n } = useTranslation('customer');
  const navigate = useNavigate();
  const { items, total, getItemCount } = useCartStore();

  const itemCount = getItemCount();
  const totalEGP = (total / 100).toFixed(1);
  const isRTL = i18n.language === 'ar';

  const handleReviewOrder = () => {
    onClose();
    navigate('/order/review');
  };

  const handleContinueOrdering = () => {
    onClose();
    navigate('/menu');
  };

  const handleBrowseMenu = () => {
    onClose();
    navigate('/menu');
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent 
        side={isRTL ? 'left' : 'right'}
        className="w-full sm:max-w-md flex flex-col p-0 [&>button]:hidden"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <SheetTitle className="text-lg font-semibold">
                {t('cart.title')}
              </SheetTitle>
              <SheetDescription className="text-sm text-muted-foreground mt-1">
                {itemCount} {itemCount === 1 ? t('cart.item') : t('cart.items')}
              </SheetDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={onClose}
              aria-label={t('common:buttons.close')}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <ScrollArea className="flex-1 px-6">
          {error && (
            <div className="py-4 px-4 mb-4 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-sm text-destructive font-medium mb-2">{error}</p>
              {onRetry && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRetry}
                  className="w-full"
                >
                  {t('common:buttons.retry', 'Retry')}
                </Button>
              )}
            </div>
          )}
          {(() => {
            const validItems = items.filter(item => item.quantity > 0);
            return validItems.length === 0 ? (
              <EmptyCartState onBrowseMenu={handleBrowseMenu} />
            ) : (
              <div 
                role="list" 
                aria-label={t('cart.title')}
                className="py-2"
              >
                {validItems.map((item) => (
                  <CartItemRow
                    key={item.id}
                    item={item}
                    onUpdateQuantity={onUpdateQuantity}
                    onRemove={onRemoveItem}
                    isUpdating={isUpdating}
                  />
                ))}
              </div>
            );
          })()}
        </ScrollArea>

        {/* Footer (only show if items exist) */}
        {items.filter(item => item.quantity > 0).length > 0 && (
          <div className="border-t px-6 py-4 space-y-4">
            {/* Subtotal */}
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-muted-foreground">
                  {t('cart.subtotal')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {itemCount} {itemCount === 1 ? t('cart.item') : t('cart.items')}
                </p>
              </div>
              <p className="text-xl font-bold">
                {totalEGP} EGP
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              <Button 
                className="w-full" 
                size="lg"
                onClick={handleReviewOrder}
                disabled={isUpdating}
              >
                {t('cart.reviewOrder')}
              </Button>
              <Button 
                variant="outline" 
                className="w-full"
                size="lg"
                onClick={handleContinueOrdering}
                disabled={isUpdating}
              >
                {t('cart.continueOrdering')}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
